/**
 * Conversations WhatsApp (Cloud API Meta → n8n → objet `conversation` Twenty).
 * Le CRM LIT ces conversations ; l'écriture reste au workflow n8n (SSOT unique).
 */
import { z } from "zod";
import type { Role } from "./roles";
import { ROLES_EMAILS } from "./emails";
import { SyncMetaSchema } from "./dto";

export const STATUTS_CONVERSATION = ["A_TRAITER", "EN_COURS", "CLOS"] as const;
export type StatutConversation = (typeof STATUTS_CONVERSATION)[number];

export const ConversationSchema = z.object({
  id: z.string(),
  nom: z.string(),
  telephone: z.string().nullable(),
  statut: z.enum(STATUTS_CONVERSATION).nullable(),
  dernierEchange: z.string().nullable(),
  dernierMessage: z.string().nullable(),
  /** Fin de la fenêtre de service Meta (24 h après le dernier message reçu). */
  fenetreJusqua: z.string().nullable(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationsResponseSchema = z.object({
  items: z.array(ConversationSchema),
  /** Numéro WhatsApp du laboratoire (affiché pour lever toute ambiguïté). */
  numeroLabo: z.string().nullable(),
  /**
   * L'envoi est-il réellement câblé côté serveur (relais n8n + jeton Meta) ?
   * Faux = l'interface ne propose PAS de répondre : jamais de bouton qui échoue.
   */
  envoiDisponible: z.boolean().default(false),
  /**
   * Le relais EXERCÉ (liste des modèles Meta, aucun envoi) — pas la présence
   * de deux variables d'env. `null` = pas sondé, jamais « ok » par défaut.
   */
  relais: z.object({ etat: z.enum(["ok", "degrade", "hs"]), detail: z.string() }).nullable().default(null),
  sync: SyncMetaSchema,
});
export type ConversationsResponse = z.infer<typeof ConversationsResponseSchema>;

/** Longueur maximale d'une réponse libre (garde-fou UI + serveur). */
export const LIMITE_MESSAGE = 900;

export const EnvoiMessageSchema = z.object({
  texte: z.string().trim().min(1, "message vide").max(LIMITE_MESSAGE, "message trop long"),
});
export type EnvoiMessage = z.infer<typeof EnvoiMessageSchema>;

export const EnvoiResultatSchema = z.object({
  ok: z.boolean(),
  messageId: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type EnvoiResultat = z.infer<typeof EnvoiResultatSchema>;

/* ------------------------------------------------ modèles approuvés par Meta */

export const ModeleSchema = z.object({
  nom: z.string(),
  langue: z.string(),
  categorie: z.string().nullable().optional(),
  corps: z.string(),
  /** Nombre de valeurs à fournir ({{1}}, {{2}}…). */
  variables: z.number().int().min(0),
});
export type Modele = z.infer<typeof ModeleSchema>;

export const ModelesResponseSchema = z.object({ items: z.array(ModeleSchema) });
export type ModelesResponse = z.infer<typeof ModelesResponseSchema>;

export const EnvoiModeleSchema = z.object({
  modele: z.string().min(1),
  variables: z.array(z.string().trim().min(1, "valeur vide").max(300)),
});
export type EnvoiModele = z.infer<typeof EnvoiModeleSchema>;

/** Corps du modèle avec les valeurs substituées — l'aperçu montre ce qui partira. */
export function rendreModele(corps: string, variables: readonly string[]): string {
  return corps.replace(/\{\{(\d+)\}\}/g, (m, n) => variables[Number(n) - 1] ?? m);
}

/** Hors fenêtre, seul un modèle passe : il ne dépend donc que du rôle et du câblage. */
export function peutEnvoyerModele(args: { roles: readonly Role[]; envoiDisponible: boolean }): boolean {
  return canVoirWhatsapp(args.roles) && args.envoiDisponible;
}

/** Fenêtre de service ouverte = on peut répondre librement (sinon : template). */
export function fenetreOuverte(fenetreJusqua: string | null, maintenant: Date): boolean {
  if (!fenetreJusqua) return false;
  return Date.parse(fenetreJusqua) > maintenant.getTime();
}

/**
 * Une réponse libre n'est possible que si TOUT est vrai : rôle autorisé, envoi
 * câblé, fenêtre de 24 h ouverte. Sinon on dit POURQUOI plutôt que d'échouer.
 */
export function raisonEnvoiImpossible(
  args: { roles: readonly Role[]; envoiDisponible: boolean; fenetreJusqua: string | null },
  maintenant: Date,
): string | null {
  if (!canVoirWhatsapp(args.roles)) return "Ton rôle ne permet pas de répondre aux praticiens.";
  if (!args.envoiDisponible) return "L'envoi n'est pas encore activé sur le serveur (jeton WhatsApp à poser).";
  if (!fenetreOuverte(args.fenetreJusqua, maintenant)) {
    return "Plus de 24 h depuis le dernier message reçu : Meta n'autorise qu'un modèle approuvé.";
  }
  return null;
}

/**
 * Mêmes rôles que les e-mails : la messagerie praticiens est un canal staff.
 *
 * La liste était RECOPIÉE ici. « Mêmes rôles que » écrit en commentaire au
 * dessus de six chaînes retapées, c'est une promesse que rien ne tient : le
 * jour où un rôle change de camp côté e-mails, WhatsApp reste en arrière sans
 * un mot. On importe la liste plutôt que de la répéter.
 */
export function canVoirWhatsapp(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_EMAILS.includes(r));
}

/**
 * Message reçu, tel que n8n `wa-inbound` le transmet au CRM pour archivage.
 *
 * `waMessageId` est l'identifiant Meta : il rend l'ingestion idempotente. Il
 * est facultatif parce qu'un rejeu manuel peut ne pas l'avoir — mais sans lui,
 * aucun dédoublonnage n'est possible, et c'est assumé plutôt que deviné.
 */
export const MessageEntrantSchema = z.object({
  conversationId: z.string().min(1),
  telephone: z.string().min(1),
  texte: z.string().trim().min(1).max(4096),
  waMessageId: z.string().min(1).nullable().optional(),
  /** horodatage Meta ; à défaut, le CRM prend l'heure de réception */
  envoyeLe: z.string().datetime({ offset: true }).optional(),
  /**
   * La pièce jointe — type tel que Meta le nomme (image, document, audio…).
   * Vocabulaire NON figé : il appartient à Meta, et refuser un type inconnu
   * ferait perdre le message ENTIER, pas seulement sa pièce.
   */
  mediaType: z.string().min(1).max(32).nullable().optional(),
  /**
   * ⚠ L'IDENTIFIANT OPAQUE DE META, JAMAIS UN NOM DE FICHIER. Meta transmet
   * aussi `document.filename`, qui porte régulièrement le nom du patient
   * (« DUPONT_Jean_14.stl ») — §0.1 de la Bible l'interdit sans condition.
   *
   * Le refus est ICI, à l'entrée, et pas seulement dans la contrainte de base :
   * n8n rejoue les envois en échec, et un 500 rejoué indéfiniment est une
   * panne silencieuse. Un 400 dit tout de suite ce qui ne va pas.
   */
  mediaRef: z
    .string()
    .regex(/^[0-9]+$/, "mediaRef doit être l'identifiant opaque de Meta, jamais un nom de fichier")
    .max(64)
    .nullable()
    .optional(),
});
export type MessageEntrant = z.infer<typeof MessageEntrantSchema>;

export const MessageHistoriqueSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  telephone: z.string(),
  direction: z.enum(["entrant", "sortant"]),
  texte: z.string(),
  modele: z.string().nullable(),
  auteur: z.string().nullable(),
  envoyeLe: z.string(),
});
export const FilConversationSchema = z.object({
  items: z.array(MessageHistoriqueSchema),
  total: z.number().int().min(0),
});
export type MessageHistorique = z.infer<typeof MessageHistoriqueSchema>;
export type FilConversation = z.infer<typeof FilConversationSchema>;
